import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import logo from "../Logo";
import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import moment from "moment";
import _ from "lodash";

import { fetchNewBuildingShipyardStat } from "../../appRedux/actions";

import {
  destination,
  point,
  transformRotate,
  featureCollection,
  convex,
  center,
} from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import CircularProgress from "../CircularProgress";
import {
  createMissingImage,
  getVesselPopupContent,
  getVesselScale,
} from "./helpers";

mapboxgl.accessToken =
  "pk.eyJ1IjoiY2hlbi1tYXJpdGltZWRhdGFzeXN0ZW1zLWNvbSIsImEiOiJjajNlNjduMzkwMHBhMzFzMjJnMGlpZmhvIn0.buGEUU37tesHnaWbKZET1A";

let COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

//generate more colors for randomness
for (let i = 0; i < 30; i++) {
  COLORS = COLORS.concat(["#0088FE", "#00C49F", "#FFBB28", "#FF8042"]);
}

function addShipyardIconSourceAndLayer(map, showLabels = true) {
  if (!map.getSource("shipyard-position-data")) {
    map.addSource("shipyard-position-data", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer("shipyard-position-layer")) {
    map.addLayer({
      id: "shipyard-position-layer",
      source: "shipyard-position-data",
      type: "symbol",
      layout: {
        "icon-size": 0.4,
        "icon-image": "shipyard",
        "text-allow-overlap": true,
        "icon-allow-overlap": true,
        "text-field": showLabels ? ["get", "name"] : "",
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 10,
        "text-offset": [1.5, 0],
        "text-anchor": "left",
      },
    });
  } else {
    // Update label visibility if layer already exists
    map.setLayoutProperty(
      "shipyard-position-layer",
      "text-field",
      showLabels ? ["get", "name"] : ""
    );
  }
}

function addVesselPolygonSourceAndLayer(map) {
  if (!map.getSource("vessel-polygon-data")) {
    map.addSource("vessel-polygon-data", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer("vessel-polygon-layer")) {
    map.addLayer({
      id: "vessel-polygon-layer",
      source: "vessel-polygon-data",
      type: "fill",
      layout: {},
      paint: {
        "fill-color": "#009688",
        "fill-opacity": 0.8,
      },
    });
  }
}

function addVesselPositionSourceAndLayer(map) {
  if (!map.getSource("vessel-position-data")) {
    map.addSource("vessel-position-data", {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
  }

  if (!map.getLayer("vessel-position-layer")) {
    map.addLayer({
      id: "vessel-position-layer",
      source: "vessel-position-data",
      type: "symbol",
      layout: {
        "icon-image": [
          "concat",
          "vessel-icon-",
          ["get", "vessel_type"],
          "-",
          ["get", "size"],
          "-",
          ["get", "load_status"],
          "-",
          ["get", "moving"],
        ],
        "icon-rotate": ["get", "heading"],
        "text-allow-overlap": true,
        "icon-allow-overlap": true,
      },
    });
  }
}

function addMissingImages(map) {
  map.on("styleimagemissing", (e) => {
    const prefix = "vessel-icon-";

    let imageName = e.id; // id of the missing image
    if (imageName.indexOf(prefix) === 0 && !map.hasImage(imageName)) {
      const [vesselType, vesselSize] = imageName // eslint-disable-line no-unused-vars
        .replace(prefix, "")
        .split("-")
        .map((m) => m);

      let scale = getVesselScale(vesselSize);
      const width = Math.ceil(16 * scale);
      const height = Math.ceil(78 * scale);
      let data = createMissingImage(imageName, width, height, prefix, scale);
      map.addImage(imageName, { width, height, data });
    }
  });

  map.loadImage(logo, function (error, image) {
    if (error) {
      throw error;
    }

    if (!map.hasImage("shipyard")) map.addImage("shipyard", image);
    if (!map.hasImage("shipyard-")) map.addImage("shipyard-", image);
  });
}

const HomePageMap = ({
  shipyards,
  news,
  vessels,
  loading,
  stat,
  currentVesselType,
  boundingBox,
  setBoundingBox,
  currentTab,
  recyclingVessels,
  programmaticMoveRef,
  currentcountryA3, // <-- add this prop
}) => {
  const [map, setMap] = React.useState(null);
  const dispatch = useDispatch();
  const hasFitBounds = useRef(false);
  const lastCountryRef = useRef(null);

  // const intl = useIntl();

  const popupRef = useRef();
  const [showLabels, setShowLabels] = React.useState(false);

  const getShipyardPopupContent = ({ properties, shipyards, news }) => {
    const shipyard = shipyards.find((s) => s.id === properties.id);

    if (news && currentTab === "news") {
      const post = news.find((n) => n.shipyard?.id === properties?.id);
      if (post) {
        const content = (
          <div style={{ textAlign: "left", width: "100%" }}>
            <b>{post.news.title}</b>
            <p>{moment(post.news.creationdate).fromNow()}</p>
          </div>
        );
        return ReactDOMServer.renderToString(content);
      }
    }

    if (!shipyard) {
      return;
    }

    const content = (
      <div style={{ textAlign: "left", width: "100%" }}>
        <b>{shipyard.name}</b>
        <p>
          {shipyard.onOrder > 0 && (
            <>
              On Order: {shipyard.onOrder}
              <br />
            </>
          )}
          {shipyard.inBuilt > 0 && (
            <>
              Delivery Expected: {shipyard.inBuilt}
              <br />
            </>
          )}
          {shipyard.delivered > 0 && (
            <>
              Delivered: {shipyard.delivered}
              <br />
            </>
          )}
        </p>
      </div>
    );
    return ReactDOMServer.renderToString(content);
  };

  //parse and set company areas and docks
  useEffect(() => {
    if (loading || !map) return;

    let shipyardPositions;

    if (currentTab === "news" && news) {
      shipyardPositions = {
        type: "FeatureCollection",
        features: news
          ?.map((post) => {
            const location = post?.shipyard?.location;
            if (location && location.length >= 2) {
              return {
                geometry: {
                  type: "Point",
                  coordinates: [location[0], location[1]],
                },
                id: "_" + post?.shipyard?.id,
                properties: {
                  id: post?.shipyard?.id,
                  name: post?.shipyard?.name,
                  type: "shipyard",
                },
              };
            } else {
              return null;
            }
          })
          .filter((feature) => feature !== null),
      };
    } else {
      shipyardPositions = {
        type: "FeatureCollection",
        features: shipyards?.map((shipyard) => ({
          geometry: {
            type: "Point",
            coordinates: [
              shipyard?.companyLocation?.x,
              shipyard?.companyLocation?.y,
            ],
          },
          id: "_" + shipyard?.id,
          properties: {
            id: shipyard?.id,
            name: shipyard?.name,
            type: "shipyard",
          },
        })),
      };
    }

    let vesselsPolygonFeatures = [];
    let targetVesselsArray =
      currentTab === "recycling" ? recyclingVessels : vessels;

    targetVesselsArray?.features?.forEach(function (feature) {
      if (!feature.properties.length || !feature.properties.width) {
        return;
      }
      let dimA = feature.properties.length / 2.0;
      let dimB = feature.properties.length / 2.0;
      let dimC = feature.properties.width / 2.0;
      let dimD = feature.properties.width / 2.0;
      if (
        feature.properties.a &&
        feature.properties.b &&
        feature.properties.c &&
        feature.properties.d
      ) {
        dimA = feature.properties.a;
        dimB = feature.properties.b;
        dimC = feature.properties.c;
        dimD = feature.properties.d;
      }
      let halfWidth = (dimC + dimD) / 2.0;
      let p1 = destination(
        point(feature.geometry.coordinates),
        dimA - halfWidth,
        0,
        { units: "meters" }
      );
      let p2 = destination(point(feature.geometry.coordinates), dimB, 180, {
        units: "meters",
      });
      let p3 = destination(point(feature.geometry.coordinates), dimC, 270, {
        units: "meters",
      });
      let p4 = destination(point(feature.geometry.coordinates), dimD, 90, {
        units: "meters",
      });
      let p5 = destination(
        point([p3.geometry.coordinates[0], p1.geometry.coordinates[1]]),
        halfWidth,
        0,
        { units: "meters" }
      );
      let p6 = destination(p5.geometry.coordinates, halfWidth, 90, {
        units: "meters",
      });
      let hull = convex(
        featureCollection([
          point([p3.geometry.coordinates[0], p2.geometry.coordinates[1]]),
          point([p4.geometry.coordinates[0], p2.geometry.coordinates[1]]),
          point([p3.geometry.coordinates[0], p1.geometry.coordinates[1]]),
          point([p4.geometry.coordinates[0], p1.geometry.coordinates[1]]),
          point(p6.geometry.coordinates),
        ])
      );
      let heading = 0;
      if (feature.properties.heading) {
        heading = feature.properties.heading;
      } else if (feature.properties.cog) {
        heading = feature.properties.cog;
      }
      vesselsPolygonFeatures.push({
        ...transformRotate(hull, heading, {
          pivot: feature.geometry.coordinates,
        }),
        properties: feature.properties,
      });
    });

    const vesselPolygon = {
      type: "FeatureCollection",
      features: vesselsPolygonFeatures,
    };

    let surveyFeatures = [];
    targetVesselsArray?.features?.forEach(function (f) {
      let days = 360;
      if (f.properties.next_survey) {
        let forecastDate = Date.parse(f.properties.next_survey);
        let dateDiff = (forecastDate - new Date()) / (1000 * 3600 * 24);
        if (dateDiff > -31 && dateDiff <= 91) {
          days = 90;
        } else if (dateDiff > 91 && dateDiff <= 182) {
          days = 180;
        }
      }
      surveyFeatures.push({
        geometry: f.geometry,
        properties: {
          ...f.properties,
          inSurveyDays: days,
        },
      });
    });

    const vesselPosition = {
      type: "FeatureCollection",
      features: surveyFeatures,
    };

    if (map.getSource("vessel-polygon-data"))
      map.getSource("vessel-polygon-data").setData(vesselPolygon);
    map.getSource("vessel-position-data").setData(vesselPosition);
    map.getSource("shipyard-position-data").setData(shipyardPositions);
    map.getSource("vessel-polygon-data").setData(vesselPolygon);
  }, [shipyards, loading, map, currentTab, news, recyclingVessels]); // eslint-disable-line react-hooks/exhaustive-deps

  //set map center
  useEffect(() => {
    if (loading || !map) return;
    map.setZoom(2);
    map.resize();
  }, [loading, map]);

  useEffect(() => {
    if (!map) return;

    // Only fit/zoom if country changed
    if (lastCountryRef.current === currentcountryA3) return;

    // Collect valid shipyard locations
    const validLocations = (shipyards || [])
      .map((c) => c?.companyLocation)
      .filter((loc) => loc && typeof loc.x === "number" && typeof loc.y === "number");

    if (validLocations.length === 0) return;

    // Calculate bounds
    let bounds = new mapboxgl.LngLatBounds();
    validLocations.forEach((loc) => bounds.extend([loc.x, loc.y]));

    // Calculate the diagonal distance of the bounds (in degrees)
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const lngDiff = Math.abs(ne.lng - sw.lng);
    const latDiff = Math.abs(ne.lat - sw.lat);

    // If shipyards are spread globally, just set a default view
    const isGlobalSpread = lngDiff > 90 || latDiff > 40; // heuristic: adjust as needed

    if (isGlobalSpread) {
      if (programmaticMoveRef) programmaticMoveRef.current = true;
      map.easeTo({
        center: [10, 53.5],
        zoom: 2,
        duration: 1000,
      });
      lastCountryRef.current = currentcountryA3;
      return;
    }

    // If only one shipyard, zoom in
    if (validLocations.length === 1) {
      if (programmaticMoveRef) programmaticMoveRef.current = true;
      map.easeTo({
        center: [validLocations[0].x, validLocations[0].y],
        zoom: 10,
        duration: 1000,
      });
      lastCountryRef.current = currentcountryA3;
      return;
    }

    // Otherwise, fit bounds
    if (programmaticMoveRef) programmaticMoveRef.current = true;
    map.fitBounds(bounds, { padding: 150, duration: 1000 });
    lastCountryRef.current = currentcountryA3;
  }, [shipyards, map, currentcountryA3]);

  //initialize the map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: "shipyard-map",
      style: "mapbox://styles/mapbox/streets-v11",
      center: [10, 53.5],
      zoom: 5,
      maxZoom: 18,
    });

    // add navigation control (the +/- zoom buttons)
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      "top-left"
    );
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.on("load", () => {
      addVesselPolygonSourceAndLayer(map);
      addVesselPositionSourceAndLayer(map);
      addShipyardIconSourceAndLayer(map, showLabels);
      addMissingImages(map);
      setMap(map);

      map.on("moveend", () => {
        // Only update bounding box if not a programmatic move
        if (programmaticMoveRef && programmaticMoveRef.current) {
          programmaticMoveRef.current = false;
          return;
        }
        setBoundingBox(map.getBounds());
      });

      map.on("flyend", () => {
        // Only update bounding box if not a programmatic move
        if (programmaticMoveRef && programmaticMoveRef.current) {
          programmaticMoveRef.current = false;
          return;
        }
        setBoundingBox(map.getBounds());
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update shipyard label visibility when toggled
  useEffect(() => {
    if (map && map.isStyleLoaded()) {
      addShipyardIconSourceAndLayer(map, showLabels);
    }
  }, [showLabels, map]);

  const clickFunc = (e) => {
    let feature = map.queryRenderedFeatures(e.point)[0];
    const type = feature?.properties?.type;
    let url = window.location.origin;

    url +=
      type === "shipyard"
        ? "/app/shipyards/" +
          `${feature?.properties?.id}-${_.kebabCase(feature?.properties?.name)}`
        : "/app/orders/" +
          `${feature?.properties?.order_id}-${
            _.kebabCase(feature?.properties?.name) ||
            _.kebabCase(feature?.properties?.companyname)
          }`;

    window.open(url, "_blank");
  };

  useEffect(() => {
    if (!map) {
      return;
    }

    // Create a popup
    let popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: "none",
    });

    popupRef.current = popup;

    let mouseEnterFunc = (e) => {
      popupRef.current.remove();
      let feature = map.queryRenderedFeatures(e.point)[0];
      map.getCanvas().style.cursor = "pointer";

      let coordinates = center(feature.geometry).geometry.coordinates.slice();
      let description =
        feature.layer.id === "shipyard-position-layer"
          ? getShipyardPopupContent({
              properties: e.features[0]?.properties,
              stat,
              shipyards,
              news,
            })
          : getVesselPopupContent(feature.properties, stat);

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      popup.setLngLat(coordinates).setHTML(description).addTo(map);
    };

    let mouseLeaveFunc = () => {
      popupRef.current.remove();
      map.getCanvas().style.cursor = "";
    };

    [
      "vessel-position-layer",
      "vessel-polygon-layer",
      "shipyard-position-layer",
    ].forEach((layer) => {
      map.on("mouseenter", layer, mouseEnterFunc);
      map.on("mouseleave", layer, mouseLeaveFunc);
      map.on("click", layer, clickFunc);
    });

    return () => {
      popupRef.current.remove();
      [
        "vessel-position-layer",
        "vessel-polygon-layer",
        "shipyard-position-layer",
      ].forEach((layer) => {
        map.off("mouseenter", layer, mouseEnterFunc);
        map.off("mouseleave", layer, mouseLeaveFunc);
        map.off("click", layer, clickFunc);
      });
    };
  }, [shipyards, map, news, currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="map-container"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <button
        style={{
          position: "absolute",
          bottom: 50,
          left: 10,
          zIndex: 2,
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: "4px",
          padding: "0.5rem 1rem",
          cursor: "pointer",
        }}
        onClick={() => setShowLabels((prev) => !prev)}
      >
        {showLabels ? "Hide Names" : "Show Names"}
      </button>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          height: "25rem",
          display: !loading ? "none" : "block",
        }}
      >
        <CircularProgress
          style={{
            height: "2rem",
            paddingTop: "6rem",
            paddingBottom: "3rem",
          }}
        />
        <h3 style={{ color: "#bbb" }}>Map data is loading...</h3>
      </div>
      <div
        id="shipyard-map"
        className="map"
        style={{
          width: "100%",
          height: "30rem",
          display: loading ? "none" : "block",
        }}
      />
    </div>
  );
};

export default HomePageMap;
